import React, { useState, useEffect, useMemo, useCallback } from "react";
import DatePicker from "react-datepicker";
import { hr } from "date-fns/locale";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "./UserReport.css";
import { ReportAPI } from "../../Api/ReportAPI";

function convertToLocalDate(reportDate) {
  if (!reportDate) return null;

  const trimmed = reportDate.trim().replace(/\.$/, "").replace(/-/g, ".");
  const parts = trimmed.split(".");
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);

  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
    return null;
  }

  const dateObj = new Date(year, month, day);
  dateObj.setHours(0, 0, 0, 0);
  return dateObj;
}

function deduplicateByDateLocation(reports) {
  const seen = new Set();
  const result = [];

  for (const report of reports) {
    const key = `${report.date}_${report.location}_${report.time}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(report);
    }
  }

  return result;
}

const ReportCalendar = ({ selectedDate, setSelectedDate, availableDates }) => (
  <div className="calendar-container">
    <label htmlFor="report-date">Odaberite datum: </label>
    <DatePicker
      id="report-date"
      selected={selectedDate}
      onChange={(date) => setSelectedDate(date)}
      includeDates={availableDates}
      locale={hr}
      dateFormat="dd MMMM yyyy"
      placeholderText="Odaberite datum izvještaja"
      className="custom-datepicker"
    />
  </div>
);

const UserReport = () => {
  const [reports, setReports] = useState([]);
  const [combinedReports, setCombinedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsError, setReportsError] = useState(null);
  const [reportPreviewUrl, setReportPreviewUrl] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [dailyResponse, combinedResponse] = await Promise.all([
          ReportAPI.getReports(),
          ReportAPI.getCombinedInventuraReports(),
        ]);

        const fetchedReports = dailyResponse.data || [];
        const fetchedCombinedReports = combinedResponse.data || [];

        fetchedReports.sort((a, b) => {
          const dateA = convertToLocalDate(a.date);
          const dateB = convertToLocalDate(b.date);

          if (!dateA || !dateB) return 0;

          const dateTimeA = new Date(dateA);
          const dateTimeB = new Date(dateB);

          if (a.time) {
            const [hA = 0, mA = 0, sA = 0] = a.time.split(":").map(Number);
            dateTimeA.setHours(hA, mA, sA, 0);
          }

          if (b.time) {
            const [hB = 0, mB = 0, sB = 0] = b.time.split(":").map(Number);
            dateTimeB.setHours(hB, mB, sB, 0);
          }

          return dateTimeB - dateTimeA;
        });

        const deduplicatedReports = deduplicateByDateLocation(fetchedReports);

        setReports(deduplicatedReports);
        setCombinedReports(fetchedCombinedReports);

        if (deduplicatedReports.length > 0) {
          setSelectedReport(deduplicatedReports[0]);
          setSelectedCalendarDate(convertToLocalDate(deduplicatedReports[0].date));
        } else {
          setSelectedReport(null);
          setSelectedCalendarDate(null);
        }

        setReportsError(null);
      } catch (error) {
        console.error(error);
        setReportsError("Došlo je do pogreške prilikom dohvaćanja izvještaja.");
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchPreviewUrl = async () => {
      if (!selectedReport) {
        setReportPreviewUrl(null);
        return;
      }

      try {
        setLoadingPreview(true);

        const response = await ReportAPI.getPreviewFileUrl(selectedReport.id);

        if (!cancelled) {
          setReportPreviewUrl(response.data.url);
          setIframeKey((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Greška pri učitavanju PDF preview URL-a:", error);
        if (!cancelled) {
          setReportsError("Greška pri učitavanju previewa izvještaja.");
          setReportPreviewUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    };

    fetchPreviewUrl();

    return () => {
      cancelled = true;
    };
  }, [selectedReport]);

  const handleDownloadAll = async () => {
    try {
      if (!selectedCalendarDate) return;

      const selectedDateStr = format(selectedCalendarDate, "dd.MM.yyyy");
      const filesForDate = combinedReports.filter((file) => {
        const normalized = (file.date || "").trim().replace(/\.$/, "").replace(/-/g, ".");
        return normalized === selectedDateStr;
      });

      for (const file of filesForDate) {
        const response = await ReportAPI.getReportFile(
          file.fileId,
          file.extension === "xlsx" ? 2 : 0
        );

        const fileType =
          file.extension === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        const blob = new Blob([response.data], { type: fileType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Greška prilikom preuzimanja:", error);
      setReportsError("Došlo je do greške prilikom preuzimanja.");
    }
  };

  const handlePrintAll = async () => {
    try {
      if (!selectedCalendarDate) return;

      const selectedDateStr = format(selectedCalendarDate, "dd.MM.yyyy");
      const pdfFilesForDate = combinedReports.filter((file) => {
        const normalized = (file.date || "").trim().replace(/\.$/, "").replace(/-/g, ".");
        return normalized === selectedDateStr && file.extension === "pdf";
      });

      for (const file of pdfFilesForDate) {
        const response = await ReportAPI.getReportFile(file.fileId, 0);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Greška prilikom ispisa:", error);
      setReportsError("Došlo je do greške prilikom ispisa.");
    }
  };

  const availableDates = useMemo(() => {
    const datesSet = new Set();

    reports.forEach((report) => {
      const localDate = convertToLocalDate(report.date);
      if (localDate) {
        datesSet.add(localDate.getTime());
      }
    });

    return Array.from(datesSet)
      .map((time) => new Date(time))
      .sort((a, b) => b.getTime() - a.getTime());
  }, [reports]);

  const filteredReports = useMemo(() => {
    if (!selectedCalendarDate) return [];

    return reports.filter((report) => {
      const reportDate = convertToLocalDate(report.date);
      return reportDate && reportDate.getTime() === selectedCalendarDate.getTime();
    });
  }, [reports, selectedCalendarDate]);

  useEffect(() => {
    if (filteredReports.length > 0) {
      setSelectedReport(filteredReports[0]);
    } else {
      setSelectedReport(null);
      setReportPreviewUrl(null);
    }
  }, [selectedCalendarDate, filteredReports]);

  const handleReportClick = useCallback((report) => {
    setSelectedReport(report);
  }, []);

  return (
    <div className="container">
      <div className="header">
        <h2>Dnevna inventura</h2>
        <ReportCalendar
          selectedDate={selectedCalendarDate}
          setSelectedDate={setSelectedCalendarDate}
          availableDates={availableDates}
        />
        {filteredReports.length > 0 && selectedReport && (
          <span>
            {format(convertToLocalDate(selectedReport.date), "dd MMMM yyyy")} u{" "}
            {selectedReport.time} ({selectedReport.location})
          </span>
        )}
      </div>

      {loadingReports ? (
        <div className="spinner-container">
          <div className="spinner"></div>
          <p>Učitavanje izvještaja...</p>
        </div>
      ) : reportsError ? (
        <div className="error-message">
          <p>{reportsError}</p>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="no-report-message">
          Još uvijek ne postoji izvještaj za taj dan
        </div>
      ) : (
        <>
          <div className="date-list">
            {filteredReports.map((report) => (
              <div
                key={report.id}
                className={`date-item ${
                  selectedReport && selectedReport.id === report.id ? "selected" : ""
                }`}
                onClick={() => handleReportClick(report)}
                role="button"
                tabIndex="0"
              >
                {report.date} {report.time} ({report.location})
              </div>
            ))}
          </div>

          <div className="buttons-container">
            <button className="preuzmi" onClick={handleDownloadAll}>
              Preuzmi sve reportove (PDF i XLSX)
            </button>
            <button className="ispisi" onClick={handlePrintAll}>
              Ispiši sve reportove (PDF)
            </button>
          </div>

          <div className="iframe-container">
            {loadingPreview ? (
              <div className="spinner-container">
                <div className="spinner"></div>
                <p>Učitavanje previewa...</p>
              </div>
            ) : (
              selectedReport &&
              reportPreviewUrl && (
                <iframe
                  key={iframeKey}
                  title={`Report for ${selectedReport.date} at ${selectedReport.time}`}
                  src={reportPreviewUrl}
                  width="100%"
                  height="1200px"
                  style={{ border: "none" }}
                />
              )
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserReport;